/**
 * WHAT THE SCREEN SAYS WHEN A FILE DOES NOT MAKE IT ONTO A RECORD. (ZEN-446)
 *
 * The defect this file exists to end: every screen that attached files did it
 * the same wrong way. The upload loop caught each failure, printed a red
 * message, carried on, saved the record without the file, and finished with a
 * green "Saved". The green one is the LAST thing on screen, so the honest
 * reading of the screen is that everything worked — while the record goes in
 * with no paperwork on it. On 2026-08-30 three bank transfer confirmations
 * were attached to a gift; the gift saved, said it worked, and the stored
 * attachment list is empty.
 *
 * For tax records that is the worst kind of failure. It is invisible until the
 * year the evidence is needed, and by then the file is long gone.
 *
 * The rule, decided by the account owner and not open here:
 *   * files that uploaded are KEPT — a save is not thrown away over one bad
 *     file, because discarding good work to punish a failure is its own loss
 *   * the failure is the LAST word, never buried under a success message
 *   * the message NAMES the file, and says plainly it was not attached
 *
 * Everything below is pure — no network, no imports, no framework. The reason
 * a failure gives and the way a file is uploaded both belong to the caller, so
 * the wording can be tested on its own and cannot drift between screens. That
 * is the whole point: this was one defect copied into four places, so the
 * answer is one set of sentences, written once.
 */

// How many failed files get named before the message stops listing them. A
// toast that names twelve files is a toast nobody reads to the end of, and the
// count in the title already says how many there were.
const MAX_NAMED = 4;

/**
 * Upload each file, keep going after a failure, and report what actually
 * happened rather than what was intended.
 *
 * `uploadOne(file)` does the sending and returns whatever the caller wants to
 * keep (a receipt id, the updated record). `toReason(error)` turns a failure
 * into the plain words a person reads. Neither belongs here.
 *
 * Returns { ok, failed } where `ok` holds the results of the uploads that
 * worked, in order, and `failed` holds { file, why } for the ones that did
 * not. Nothing is thrown: a refused file is an outcome to report, not a crash.
 */
export async function runUploads(files, uploadOne, toReason) {
  const ok = [];
  const failed = [];
  for (const file of files || []) {
    try {
      ok.push(await uploadOne(file));
    } catch (e) {
      failed.push({ file, why: toReason(e) });
    }
  }
  return { ok, failed };
}

/**
 * The sentences a person reads when at least one file did not attach.
 *
 * Returns null when nothing failed — the caller then shows its own success
 * message, which is the ONLY path on which a success message is honest.
 *
 *   noun     what was saved, in the words a family uses: "gift",
 *            "529 distribution", "expense"
 *   total    how many files were staged
 *   attached how many of them made it on
 *   failed   [{ file, why }] from runUploads
 *   retry    one sentence telling the person how to get the file on, which
 *            differs by screen: some keep the box open with the file still
 *            listed, some close and are re-opened later
 */
export function attachmentFailureMessage({ noun, total, attached, failed, retry }) {
  const misses = failed || [];
  if (misses.length === 0) return null;

  const name = (m) => (m.file && m.file.name) || "The file";

  // "DID NOT SAVE", never "was not attached". The person DID attach it — they
  // chose it, and they can see it sitting in the list. Telling them it was not
  // attached contradicts what is in front of them and reads as nonsense. What
  // failed is the SAVING of it, which is also what they need to retry.
  const title = misses.length === 1
    ? `${name(misses[0])} did not save`
    : `${misses.length} files did not save`;

  // What DID happen to the record, first, so nobody reads this as "nothing
  // saved" and enters the gift a second time.
  const saved = attached > 0
    ? `The ${noun} is saved, and ${attached} of the ${total} files you attached saved with it.`
    : `The ${noun} is saved, but the ${total === 1 ? "file" : `${total} files`} you attached did NOT save.`;

  // The server's OWN sentence, per file, passed through untouched. Those
  // sentences already say what to do about it — "Take the photo at a lower
  // resolution, or crop it", "already uploaded as X on DATE" — and inventing
  // a shorter reason here would throw away the only actionable part.
  // Some of those sentences end in a full stop and some do not, so the
  // punctuation is taken off and put back once. Otherwise a reason that came
  // with its own ends the line on ".." — small, but it is the kind of thing
  // that makes a message look machine-made at the exact moment a person needs
  // to trust it.
  const trim = (why) => String(why || "").replace(/[.;\s]+$/, "");
  const named = misses.slice(0, MAX_NAMED)
    .map((m) => `${name(m)} — ${trim(m.why)}`)
    .join("; ");
  const rest = misses.length - MAX_NAMED;
  const why = rest > 0 ? `${named}; and ${rest} more.` : `${named}.`;

  return { title, description: [saved, why, retry].filter(Boolean).join(" ") };
}
